import { useEffect, useState } from 'react'
import { useSearchParams, Navigate, Link } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'

export default function AuthVerifyPage() {
  const [searchParams] = useSearchParams()
  const { refreshUser } = useAuth()
  const [status, setStatus] = useState<'verifying' | 'success' | 'error'>('verifying')
  const [error, setError] = useState('')

  const token = searchParams.get('token')
  const verified = searchParams.get('verified')

  useEffect(() => {
    // If we already have verified=true, just refresh the user
    if (verified === 'true') {
      refreshUser()
      setStatus('success')
      return
    }

    // If no token, show error
    if (!token) {
      setStatus('error')
      setError('No verification token provided')
      return
    }

    // The API handles verification via redirect, so if we're here with a token,
    // the user was redirected here by the API after successful verification
    refreshUser()
    setStatus('success')
  }, [token, verified, refreshUser])

  if (status === 'success') {
    return <Navigate to="/profile?verified=true" replace />
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center px-4">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-red-500/10 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="font-display text-2xl text-archive-cream mb-4">
            Verification Failed
          </h1>
          <p className="text-archive-muted mb-6">{error}</p>
          <Link
            to="/login"
            className="inline-block px-6 py-3 bg-archive-gold text-archive-black font-semibold rounded-lg hover:bg-archive-amber transition-colors"
          >
            Try Again
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-archive-gold border-t-transparent mx-auto mb-6" />
        <h1 className="font-display text-2xl text-archive-cream mb-4">
          Verifying...
        </h1>
        <p className="text-archive-muted">
          Please wait while we sign you in.
        </p>
      </div>
    </div>
  )
}
